import React from 'react';
import {withRouter, WithRouterProps} from 'react-router';
import {isNumber} from 'lodash';

import {Button, Icon, Text} from '@albert-io/atomic';

import {ProficiencyChartBadges} from 'client/Reports/components/Table/Columns/ProficiencyChart/ProficiencyChart';

// TODO: replace with real BE data once implemented
const DUMMY_USER_HAS_ACCESS = true;

interface SkillItem {
  id: string;
  name: string;
  code: string;
  proficiencyLevel: number | null;
}

interface SkillSection {
  label: string;
  items: SkillItem[];
}

interface SkillGroup {
  subjectName: string;
  browseUrl: string;
  sections: SkillSection[];
}

const DUMMY_SKILL_GROUPS: SkillGroup[] = [
  {
    subjectName: 'Adaptive ELA',
    browseUrl: '/adaptive-subjects/adaptive-grammar-skills/grammar-mechanics',
    sections: [
      {
        label: 'Writing',
        items: [
          {id: 'dummy-1', name: 'Skill Practice', code: 'C0D3', proficiencyLevel: 2},
          {
            id: 'dummy-2',
            name: 'Identify parts of speech in context',
            code: 'C0D4',
            proficiencyLevel: 1
          },
          {
            id: 'dummy-3',
            name: 'Apply comma rules in compound sentences',
            code: 'C0D5',
            proficiencyLevel: null
          }
        ]
      },
      {
        label: 'Reading',
        items: [
          {
            id: 'dummy-4',
            name: 'View sub-expressions as single entities',
            code: 'C0D6',
            proficiencyLevel: 3
          },
          {id: 'dummy-5', name: 'Skill Practice', code: 'C0D7', proficiencyLevel: null}
        ]
      }
    ]
  }
];

type FilterOption = 'All' | 'To Do';

interface StudentSkillPathListProps {
  filter?: FilterOption;
}

// Benchmarked from LeafNode in AdaptiveSubjects
interface SkillPathItemProps extends WithRouterProps<{}> {
  guideLevelId: string;
  name: string;
  fragment?: string | null;
  proficiencyLevel?: number | null;
}

const SkillPathItemInner = ({
  router,
  guideLevelId,
  name,
  fragment,
  proficiencyLevel
}: SkillPathItemProps) => {
  const handleClick = () => {
    router.push(`/adaptive/skill/${guideLevelId}`);
  };

  return (
    <div
      className='flex cursor-pointer items-center justify-between hover:bg-background-subtle'
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      <div className=''>
        <Text size='m' color='primary' className='mr-x1'>
          {name}
        </Text>
        <Text size='m' color='tertiary'>
          ({fragment})
        </Text>
      </div>
      <div className='flex shrink-0 items-center gap-x1'>
        <ProficiencyChartBadges
          proficiencyLevel={proficiencyLevel}
          showNotStarted={false}
          showLabel={false}
          badgeSize={16}
          iconGap={0}
          className='proficiency-chart__student_browsing'
          withTooltip={isNumber(proficiencyLevel)}
        />
      </div>
    </div>
  );
};

const SkillPathItem = withRouter(SkillPathItemInner);

interface BrowseAllSkillsLinkProps extends WithRouterProps<{}> {
  href: string;
}

const BrowseAllSkillsLinkInner = ({router, href}: BrowseAllSkillsLinkProps) => (
  <Button
    color='secondary'
    variant='outlined'
    size='s'
    onClick={() => router.push(href)}
    roundness='pill'
    className='w-[149px]'
  >
    <Text size='s' color='inherit' className='font-normal'>
      Browse all skills
    </Text>
    <Icon className='ml-x1-quarter' icon={['far', 'arrow-up-right']} />
  </Button>
);

const BrowseAllSkillsLink = withRouter(BrowseAllSkillsLinkInner);

// Empty state: user does not have access to this subject
function SkillPathNoAccessState() {
  return (
    <div className='flex flex-col items-center justify-center gap-x2 border border-border-subtle p-x4 text-center'>
      <div className='flex h-[40px] w-[40px] items-center justify-center rounded-full bg-background-bold'>
        <Icon icon={['far', 'sparkles']} color='tertiary' />
      </div>
      <Text size='m' color='secondary'>
        Adaptive subjects and skills will appear here if you have access.{' '}
      </Text>
    </div>
  );
}

// TODO: wire filter prop to BE when implementation is ready
export function StudentSkillPathList({filter: _filter}: StudentSkillPathListProps) {
  // user does not have access
  if (!DUMMY_USER_HAS_ACCESS) {
    return (
      <div className='flex flex-col gap-x4 border-t border-t-border-subtle p-x4'>
        <SkillPathNoAccessState />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-x4 border-t border-t-border-subtle p-x4'>
      {DUMMY_SKILL_GROUPS.map((group) => (
        <div key={group.subjectName} className='flex flex-col gap-x2'>
          <div className='flex items-center gap-x2'>
            <div className='flex h-[40px] w-[40px] items-center justify-center rounded-full bg-[var(--color-background-special-subtle)]'>
              <Icon icon='sparkles' className='content-special!' />
            </div>
            <div className='flex flex-col'>
              <Text size='l' color='primary'>
                {group.subjectName}
              </Text>
              {group.sections.length === 0 && (
                <Text size='s' color='tertiary'>
                  You can practice any skills within this subject.
                </Text>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-x0-half pl-x7'>
            {group.sections.map((section) => (
              <div key={section.label} className='mb-x1 flex flex-col'>
                <div className='pb-x0-half'>
                  <Text size='s' color='primary' bold>
                    {section.label}
                  </Text>
                </div>
                <div className='flex flex-col gap-x0-half'>
                  {section.items.map((item) => (
                    <SkillPathItem
                      key={item.id}
                      guideLevelId={item.id}
                      name={item.name}
                      fragment={item.code}
                      proficiencyLevel={item.proficiencyLevel}
                    />
                  ))}
                </div>
              </div>
            ))}
            <BrowseAllSkillsLink href={group.browseUrl} />
          </div>
        </div>
      ))}
    </div>
  );
}
